import React, { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { Form, Field } from "react-final-form";
import { useMutation, useQuery } from "@apollo/client";
import { ReactSelectAdapter } from "../reuseableComponents/forms/ReactSelectAdapter";

import Label from "../reuseableComponents/forms/Label";
import { UPDATE_USER } from "../../queries/ddwAdminQrys/UserManagementQrys";
import { GET_ALL_SYSTEM_INSTITUTION_LHD } from "../../queries/ddwAdminQrys/PortalUserManagementQueries";
import { ADD_NEW_PORTAL_USER } from "../../queries/ddwAdminQrys/PortalUserManagementQueries";
import { UserContext } from "../reuseableComponents/auth/UserContext";
import ButtonSubmit from "../reuseableComponents/buttons/ButtonSubmit";
import ButtonProcessing from "../reuseableComponents/buttons/ButtonProcessing";

import { composeValidators, required } from "../reuseableComponents/forms/formValidation";

const CreatePortalAccountForm = () => {
  const [defaultSystems, setDefaultSystems] = useState(null);
  const [defaultInstitutions, setDefaultInstitutions] = useState(null);
  const [defaultLHDs, setDefaultLHDs] = useState(null);
  const [userSystems, setUserSystems] = useState(null);
  const [userInstitutions, setUserInstitutions] = useState(null);
  const [userLHDs, setUserLHDs] = useState(null);
  const [formComplete, setFormComplete] = useState(false);

  const { data } = useQuery(GET_ALL_SYSTEM_INSTITUTION_LHD);
  const { user } = useContext(UserContext);

  const onComplete = () => {
    // Mutate finished, set form to completed.
    setFormComplete(true);
  };

  const [mutatePortalUser, mutatePortalUserProps] = useMutation(ADD_NEW_PORTAL_USER, {
    onCompleted: onComplete
  });

  const [mutateUser, mutateUserProps] = useMutation(UPDATE_USER, {});

  //Submit form
  const onSubmit = (newUser) => {
    // To set isPortalUser on user account
    mutateUser({
      variables: {
        umdId: user.umdId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        isStaff: user.isStaff,
        isSysAdmin: user.isSysAdmin,
        isPortalUser: true,
        isActive: user.isActive
      }
    });

    // To create portal user account
    mutatePortalUser({
      variables: {
        secUserAccountPk: user.umdId,
        accountName: newUser.accountName,
        acctOwnerUmdId: user.umdId,
        accountPurpose: newUser.accountPurpose,
        createdUser: user.email,
        status: "Pending",
        lastUpdatedUser: user.email,
        systems: userSystems,
        institutions: userInstitutions,
        lhds: userLHDs
      }
    });
  };

  const handleWaterSystemChange = (value) => {
    setDefaultSystems(value);
    const sysArrayList = [];

    value.forEach((element) => {
      sysArrayList.push({
        systemId: parseInt(element.value)
      });
    });

    setUserSystems(sysArrayList);
  };

  const handleInstitutionChange = (value) => {
    setDefaultInstitutions(value);
    const insArrayList = [];

    value.forEach((element) => {
      insArrayList.push({
        institutionCode: parseInt(element.value)
      });
    });

    setUserInstitutions(insArrayList);
  };

  const handleLHDChange = (value) => {
    setDefaultLHDs(value);
    const lhdArrayList = [];

    value.forEach((element) => {
      lhdArrayList.push({
        lhdId: parseInt(element.value)
      });
    });

    setUserLHDs(lhdArrayList);
  };

  const handleSystemDefaults = () => {
    const arrayList = [];
    const sysArrayList = [];
    setDefaultSystems(arrayList);
    setUserSystems(sysArrayList);
  };

  const handleInstitutionDefaults = () => {
    const arrayList = [];
    const insArrayList = [];
    setDefaultInstitutions(arrayList);
    setUserInstitutions(insArrayList);
  };

  const handleLHDDefaults = () => {
    const arrayList = [];
    const lhdArrayList = [];
    setDefaultLHDs(arrayList);
    setUserLHDs(lhdArrayList);
  };

  useEffect(() => {
    handleSystemDefaults();
    handleInstitutionDefaults();
    handleLHDDefaults();
  }, []);

  return (
    <>
      {!formComplete ? (
        <Row>
          <Col>
            <Form
              keepDirtyOnReinitialize
              onSubmit={onSubmit}
              initialValues={{
                secUserAccountPk: user.umdId,
                accountName: "",
                acctOwnerUmdId: user.umdId,
                accountPurpose: "",
                status: "",
                lastUpdatedUser: user.email,
                systems: defaultSystems,
                institutions: defaultInstitutions,
                LHDs: defaultLHDs
              }}
              render={({ handleSubmit, form, pristine, submitting, values }) => (
                <form onSubmit={handleSubmit}>
                  <Row>
                    <Col>
                      <Label htmlFor='accountName' label='Account Name' required />
                      <Field name='accountName' validate={composeValidators(required)}>
                        {({ input, meta }) => (
                          <div>
                            <input className='form-control m-2' {...input} type='text' />
                            {meta.error && meta.touched && <span className='error'>{meta.error}</span>}
                          </div>
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <br />
                  <Row>
                    <Col>
                      <Label htmlFor='accountPurpose' label='Account Description / Justification' required />
                      <Field name='accountPurpose' validate={composeValidators(required)}>
                        {({ input, meta }) => (
                          <div>
                            <input className='form-control m-2' {...input} type='text' />
                            {meta.error && meta.touched && <span className='error'>{meta.error}</span>}
                          </div>
                        )}
                      </Field>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <br />
                      <h4>Select the entities this account will be associated with</h4>
                      <br />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Label htmlFor='waterSystems' label='Water Systems' />
                      {data ? (
                        <Field
                          name='systems'
                          component={ReactSelectAdapter}
                          options={data.getWaterSystemMenu}
                          className='mb-2'
                          isMulti
                          value={defaultSystems}
                          onChange={handleWaterSystemChange}
                        />
                      ) : (
                        <p>Loading...</p>
                      )}
                    </Col>
                    <Col>
                      <Label htmlFor='lhdOptions' label='Local Health Departments' />
                      {data ? (
                        <Field
                          name='LHDs'
                          component={ReactSelectAdapter}
                          options={data.getAllLocalHealthDepartments}
                          className='mb-2'
                          isMulti
                          value={defaultLHDs}
                          onChange={handleLHDChange}
                        />
                      ) : (
                        <p>Loading...</p>
                      )}
                    </Col>
                    <Col>
                      <Label label='Institutions / Entities' htmlFor='institutionOptions' />
                      {data ? (
                        <Field
                          name='institutions'
                          component={ReactSelectAdapter}
                          options={data.getAllInstitutions}
                          className='mb-2'
                          isMulti
                          value={defaultInstitutions}
                          onChange={handleInstitutionChange}
                        />
                      ) : (
                        <p>Loading...</p>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {mutateUserProps.loading || mutatePortalUserProps.loading ? (
                        <ButtonProcessing
                          id='ddw-primary'
                          style={{ float: "right" }}
                          disabled={mutateUserProps.loading || mutatePortalUserProps.loading}
                        />
                      ) : (
                        <ButtonSubmit
                          id='ddw-primary'
                          style={{ float: "right" }}
                          onClick={() => onSubmit(values)}
                          disabled={
                            pristine ||
                            submitting ||
                            mutatePortalUserProps.loading ||
                            user.isPortalUser ||
                            mutateUserProps.loading
                          }
                        />
                      )}
                    </Col>
                  </Row>

                  {user.isPortalUser ? (
                    <p class='error'>Your account is already associated with a portal account.</p>
                  ) : (
                    ""
                  )}
                </form>
              )}
            />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col>
            <p>Your request has been submitted. Please check your email for instructions.</p>
          </Col>
        </Row>
      )}
    </>
  );
};

export default CreatePortalAccountForm;
